/** TanStack Query hooks for the OMS operator UI (spec §8.1).
 *  Mirrors the engineering/secondary hook shapes (shared client, Bearer auth).
 *
 *  - List/detail/meters/timeline/critical/reliability/unmatched READ hooks.
 *  - Status + active-list + map REFETCH on a ~30s interval (live operator view).
 *  - Cursor pagination: list hooks take a `cursor` and keep the previous page
 *    visible while the next loads (placeholderData: keepPreviousData).
 *  - Mutation hooks (create/patch/close/cancel/confirm/merge/split) invalidate
 *    every OMS read cache on success; callers branch on a 409 stale write via
 *    isConflictError from the shared client. */

import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import {
  cancelOutage,
  getPowertrxPreview,
  listCauses,
  closeOutage,
  confirmOutage,
  confirmSourceDevice,
  createOutage,
  getOmsStatus,
  getOutage,
  getOutageConvergence,
  getOutageCorrelated,
  getOutageMap,
  getOutageTimeline,
  getReliabilityMetrics,
  listAffectedMeters,
  listCriticalCustomers,
  listFeederProtectiveDevices,
  listOutageHistory,
  listOutages,
  listUnmatchedSignals,
  listUnmatchedSummary,
  mergeOutages,
  overrideSourceDevice,
  patchOutage,
  splitOutage,
  type OutageSort,
} from '../api/oms.ts'
import type {
  OutageCancel,
  OutageClose,
  OutageConfirm,
  OutageCreate,
  OutageMerge,
  OutagePatch,
  OutageSplit,
  SourceDeviceConfirm,
  SourceDeviceOverride,
} from '../types/oms.ts'

/** Live-view refetch cadence for status/list/map (spec §8: ~30s auto-refresh). */
const OMS_LIVE_REFETCH_MS = 30_000

// =========================================================================
// Reads
// =========================================================================

/** Active/filtered outage list (cursor-paged). Auto-refreshes every ~30s and
 *  keeps the current page visible while the next cursor loads. */
export function useOutages(params?: {
  status?: string
  feeder_id?: number
  scope?: string
  priority?: string
  cause_code?: string
  sort?: OutageSort
  limit?: number
  cursor?: string | null
  roots_only?: boolean
  parent_id?: number
}) {
  return useQuery({
    queryKey: ['oms', 'outages', params ?? {}],
    queryFn: () => listOutages(params),
    refetchInterval: OMS_LIVE_REFETCH_MS,
    placeholderData: keepPreviousData,
  })
}

/** Closed/cancelled history (cursor-paged). Not on the live interval. */
export function useOutageHistory(params?: {
  cause_code?: string
  feeder_id?: number
  limit?: number
  cursor?: string | null
}) {
  return useQuery({
    queryKey: ['oms', 'outage-history', params ?? {}],
    queryFn: () => listOutageHistory(params),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  })
}

/** Single outage detail. */
export function useOutage(outageId: number | null) {
  return useQuery({
    queryKey: ['oms', 'outage', outageId],
    queryFn: () => getOutage(outageId!),
    enabled: outageId !== null,
  })
}

/** Paged affected meters/addresses for an outage (PII). */
export function useAffectedMeters(
  outageId: number | null,
  params?: { q?: string; limit?: number; cursor?: string | null },
) {
  return useQuery({
    queryKey: ['oms', 'affected-meters', outageId, params ?? {}],
    queryFn: () => listAffectedMeters(outageId!, params),
    enabled: outageId !== null,
    placeholderData: keepPreviousData,
  })
}

/** Event timeline (oms_restoration_log) for an outage. */
export function useOutageTimeline(outageId: number | null) {
  return useQuery({
    queryKey: ['oms', 'timeline', outageId],
    queryFn: () => getOutageTimeline(outageId!),
    enabled: outageId !== null,
  })
}

/** Critical-customer PHI detail — paged (phi_authorized; every access audited).
 *  Disabled by default; pass `enabled` only when the PHI panel is opened. */
export function useCriticalCustomers(
  outageId: number | null,
  params?: { limit?: number; cursor?: string | null },
  enabled = false,
) {
  return useQuery({
    queryKey: ['oms', 'critical-customers', outageId, params ?? {}],
    queryFn: () => listCriticalCustomers(outageId!, params),
    enabled: enabled && outageId !== null,
    retry: false,
  })
}

/** GeoJSON aggregate outage map (color-by-scope). Auto-refreshes every ~30s. */
export function useOutageMap(params?: { status?: string }) {
  return useQuery({
    queryKey: ['oms', 'map', params ?? {}],
    queryFn: () => getOutageMap(params),
    refetchInterval: OMS_LIVE_REFETCH_MS,
  })
}

/** Pull heartbeat / data-as-of freshness for the banner. ~30s refetch. */
export function useOmsStatus() {
  return useQuery({
    queryKey: ['oms', 'status'],
    queryFn: getOmsStatus,
    refetchInterval: OMS_LIVE_REFETCH_MS,
  })
}

/** Reliability metrics (SAIDI/SAIFI/CAIDI). Read-only. */
export function useReliabilityMetrics(params?: {
  period?: string
  scope?: string
  feeder_id?: number
  limit?: number
}) {
  return useQuery({
    queryKey: ['oms', 'reliability', params ?? {}],
    queryFn: () => getReliabilityMetrics(params),
    staleTime: 60_000,
  })
}

/** Unmatched-meter / unlinked report (oms_admin). */
export function useUnmatchedSignals(params?: { limit?: number }) {
  return useQuery({
    queryKey: ['oms', 'unmatched', params ?? {}],
    queryFn: () => listUnmatchedSignals(params),
    staleTime: 60_000,
  })
}

/** Distinct unmatched meter numbers (oms_admin crosswalk worklist). */
export function useUnmatchedSummary(params?: { limit?: number }) {
  return useQuery({
    queryKey: ['oms', 'unmatched-summary', params ?? {}],
    queryFn: () => listUnmatchedSummary(params),
    staleTime: 60_000,
  })
}

/** Active protective devices on a feeder (the override picker). Only fetches when
 *  a feeder id is supplied (the override dialog is open). */
export function useFeederProtectiveDevices(feederId: number | null) {
  return useQuery({
    queryKey: ['oms', 'feeder-devices', feederId],
    queryFn: () => listFeederProtectiveDevices(feederId!),
    enabled: feederId !== null,
    staleTime: 60_000,
  })
}

/** Other active events converging on this one's device chain (Merge & assign). */
export function useOutageConvergence(outageId: number | null) {
  return useQuery({
    queryKey: ['oms', 'convergence', outageId],
    queryFn: () => getOutageConvergence(outageId!),
    enabled: outageId !== null,
    refetchInterval: OMS_LIVE_REFETCH_MS,
  })
}

/** Time-correlated events for the merge hint. Live interval like convergence. */
/** PowerTRX's primary-cause tree. Static server data: fetched once. */
export function useCauses() {
  return useQuery({
    queryKey: ['oms', 'causes'],
    queryFn: listCauses,
    staleTime: Infinity,
  })
}

/** PowerTRX export preview for a local date range (dispatcher). Refetches
 *  after any OMS mutation (the ['oms'] invalidation), e.g. a cause save. */
export function usePowertrxPreview(
  params: { start: string; end: string; include_exported?: boolean } | null,
) {
  return useQuery({
    queryKey: ['oms', 'powertrx', params],
    queryFn: () => getPowertrxPreview(params!),
    enabled: params !== null,
    placeholderData: keepPreviousData,
  })
}

export function useOutageCorrelated(outageId: number | null) {
  return useQuery({
    queryKey: ['oms', 'correlated', outageId],
    queryFn: () => getOutageCorrelated(outageId!),
    enabled: outageId !== null,
    refetchInterval: OMS_LIVE_REFETCH_MS,
  })
}

// =========================================================================
// Mutations (dispatcher) — invalidate every OMS read on success
// =========================================================================

/** Invalidate every OMS read cache a mutation can affect (list/detail/map/
 *  timeline/meters/history). Broad by design: an operator action re-parents or
 *  closes events that show up across all of them. */
function useInvalidateOms() {
  const queryClient = useQueryClient()
  return () => {
    queryClient.invalidateQueries({ queryKey: ['oms'] })
  }
}

/** Manual outage create (dispatcher). Invalidates all OMS reads on success. */
export function useCreateOutage() {
  const invalidate = useInvalidateOms()
  return useMutation({
    mutationFn: (body: OutageCreate) => createOutage(body),
    onSuccess: invalidate,
  })
}

/** PATCH operator-owned fields — optimistic on updated_at (409 on stale). */
export function usePatchOutage() {
  const invalidate = useInvalidateOms()
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: OutagePatch }) =>
      patchOutage(id, body),
    onSuccess: invalidate,
  })
}

/** Close an outage (dispatcher; optimistic, 409 on stale). */
export function useCloseOutage() {
  const invalidate = useInvalidateOms()
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: OutageClose }) =>
      closeOutage(id, body),
    onSuccess: invalidate,
  })
}

/** Cancel as false positive (dispatcher; optimistic, 409 on stale). */
export function useCancelOutage() {
  const invalidate = useInvalidateOms()
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: OutageCancel }) =>
      cancelOutage(id, body),
    onSuccess: invalidate,
  })
}

/** Confirm → reportable_for_reliability (dispatcher; optimistic, 409 on stale). */
export function useConfirmOutage() {
  const invalidate = useInvalidateOms()
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: OutageConfirm }) =>
      confirmOutage(id, body),
    onSuccess: invalidate,
  })
}

/** Merge/roll-up events under a primary (dispatcher; optimistic, 409 on stale). */
export function useMergeOutages() {
  const invalidate = useInvalidateOms()
  return useMutation({
    mutationFn: (body: OutageMerge) => mergeOutages(body),
    onSuccess: invalidate,
  })
}

/** Split meters off an event into a new event (dispatcher; optimistic). */
export function useSplitOutage() {
  const invalidate = useInvalidateOms()
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: OutageSplit }) =>
      splitOutage(id, body),
    onSuccess: invalidate,
  })
}

/** Confirm the engine-suggested source device (dispatcher; optimistic, 409 on stale). */
export function useConfirmSourceDevice() {
  const invalidate = useInvalidateOms()
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: SourceDeviceConfirm }) =>
      confirmSourceDevice(id, body),
    onSuccess: invalidate,
  })
}

/** Override the suggested source device with a picked device (dispatcher). */
export function useOverrideSourceDevice() {
  const invalidate = useInvalidateOms()
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: SourceDeviceOverride }) =>
      overrideSourceDevice(id, body),
    onSuccess: invalidate,
  })
}
